import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Pill, StateCard } from '../../components/StateCard';
import { SiteHead, formatDate } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface LegalPayload {
  document_key: string; title: string; body: string; version: number;
  locale: string; consent_required: boolean; effective_at: string | null;
}

/** Only documents that are actually published; a tab to a 404 is worse than no tab. */
const DOCUMENTS = [
  { key: 'terms', label: '이용약관' },
  { key: 'privacy', label: '개인정보 처리방침' },
  { key: 'licenses', label: '오픈소스 고지' },
];

/** P014 — legal documents. The effective version is what is shown; there is no unversioned copy. */
export function PublicLegal() {
  const { documentKey } = useParams();
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale') ?? 'ko';

  const document = useResource<LegalPayload>(
    (signal) => api.public(`/api/v1/legal/${documentKey}`, { signal, query: { locale } }) as Promise<ApiEnvelope<LegalPayload>>,
    [documentKey, locale],
    { emptyWhen: () => false },
  );

  const data = document.data;

  return (
    <>
      <div className="chips" style={{ marginBottom: 24 }}>
        {DOCUMENTS.map((item) => (
          <Link key={item.key} className="chip" to={`/legal/${item.key}`} aria-current={documentKey === item.key ? 'page' : undefined}>
            {item.label}
          </Link>
        ))}
      </div>

      <StateCard state={document.state} error={document.error} onRetry={document.reload}>
        {data && (
          <article className="article">
            <SiteHead
              eyebrow="약관"
              title={data.title}
              meta={
                <>
                  <Pill tone="neutral">v{data.version}</Pill>
                  <span>시행 {formatDate(data.effective_at)}</span>
                  {data.consent_required && <Pill tone="info">동의 필요</Pill>}
                </>
              }
            />

            <div className="article__body">{data.body}</div>

            <div className="card" style={{ marginTop: 40 }}>
              <p style={{ margin: '0 0 12px' }}>내 데이터 확인·내보내기·정정·삭제 요청</p>
              <Link className="btn" to="/privacy/requests">내 데이터 요청</Link>
            </div>
          </article>
        )}
      </StateCard>
    </>
  );
}
